import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@repo/ui/src/components/ui/empty';

/**
 * Three-kind empty state for list screens (design doc 2026-08-22-states §1):
 *
 * - `teach`: nothing has ever been created here — explain what this list is
 *   for and offer the one action that fills it.
 * - `filterMiss`: data exists, but the current filter/window hides all of
 *   it — offer to clear back to the default.
 * - `searchMiss`: a text search matched nothing — no CTA, just a hint about
 *   how the search works.
 *
 * A thin wrapper over shadcn `Empty` rather than a replacement for it: pages
 * that need a bespoke empty layout can still reach for `Empty` directly.
 */
type EmptyStateProps =
  | {
      kind: 'teach';
      icon: LucideIcon;
      title: string;
      body: string;
      action?: { label: string; onClick?: () => void; to?: string };
    }
  | { kind: 'filterMiss'; title: string; onClear: () => void; body?: string }
  | { kind: 'searchMiss'; query: string; title?: string; hint?: string };

export function EmptyState(props: EmptyStateProps) {
  const { t } = useTranslation('common');

  if (props.kind === 'teach') {
    const Icon = props.icon;
    return (
      <Empty className="border-none p-0 py-10 md:p-0 md:py-10">
        <EmptyHeader>
          <EmptyMedia variant="icon" className="rounded-full bg-brand-50 text-brand-700">
            <Icon />
          </EmptyMedia>
          <EmptyTitle>{props.title}</EmptyTitle>
          <EmptyDescription>{props.body}</EmptyDescription>
        </EmptyHeader>
        {props.action && (
          <EmptyContent>
            {props.action.to ? (
              <Button asChild size="sm">
                <Link to={props.action.to}>{props.action.label}</Link>
              </Button>
            ) : (
              <Button size="sm" onClick={props.action.onClick}>
                {props.action.label}
              </Button>
            )}
          </EmptyContent>
        )}
      </Empty>
    );
  }

  if (props.kind === 'filterMiss') {
    return (
      <Empty className="border-none p-0 py-10 md:p-0 md:py-10">
        <EmptyHeader>
          <EmptyTitle>{props.title}</EmptyTitle>
          <EmptyDescription>{props.body ?? t('empty.filterBody')}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button variant="outline" size="sm" onClick={props.onClear}>
            {t('empty.clearFilters')}
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  // searchMiss
  return (
    <Empty className="border-none p-0 py-10 md:p-0 md:py-10">
      <EmptyHeader>
        <EmptyTitle>{props.title ?? t('empty.searchTitle', { query: props.query })}</EmptyTitle>
        <EmptyDescription>{props.hint ?? t('empty.searchHint')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
